import { apiFetch, expectJsonBody } from "@common/lib/api";
import type { PlanProduct, AddonProduct, OneTimeFeeProduct, ProductCatalog } from "@/features/admin/checkout/types";

/** Metadata keys from Stripe product metadata (values are strings). */
const METADATA_KEYS = {
    TYPE: "type",
    /** API/DB use max_discount; we also accept discount_max */
    DISCOUNT_MAX: "max_discount",
    DISCOUNT_MAX_ALT: "discount_max",
    INCLUDED_VISITS: "included_visits",
    VISITS_PER_UNIT: "visits_per_unit",
    /** Happy visits (monthly) from product metadata; summed for total monthly visits */
    HAPPY_VISITS: "happy_visits",
    AVAILABLE_FOR_PLANS: "available_for_plans",
} as const;

/** metadata.type value for a one-time implementation fee product. */
const ONE_TIME_FEE = "one_time_fee";

/** Recurring can be object from API or jsonb from DB (interval inside). */
export interface StripeRecurring {
    interval?: string;
    interval_count?: number;
}

export interface StripePriceApi {
    id: string;
    unit_amount: number | null;
    /** From Stripe/DB: { interval: "month", interval_count: 1 } or json string */
    recurring?: StripeRecurring | string | null;
    active?: boolean;
    /** Stripe price type: "recurring" or "one_time". */
    type?: string;
}

export interface StripeProductApi {
    id: string;
    name: string;
    description: string | null;
    default_price?: string | null;
    /** When API returns products with embedded prices */
    prices?: StripePriceApi[];
    metadata?: Record<string, string> | null;
}

/** API returns array of { product, prices } (not flat products). */
export interface StripeProductWithPrices {
    product: StripeProductApi;
    prices?: StripePriceApi[];
}

export type StripeProductsResponse =
    | StripeProductWithPrices[]
    | StripeProductApi[]
    | { products: StripeProductWithPrices[] | StripeProductApi[]; annual_discount_percent?: number };

function parseMetaNumber(metadata: Record<string, string> | null | undefined, key: string): number {
    if (!metadata) return 0;
    const value = metadata[key] ?? metadata[key.toLowerCase()] ?? metadata[key.toUpperCase()];
    if (value === undefined || value === "") return 0;
    const n = parseInt(String(value).trim(), 10);
    return isNaN(n) ? 0 : n;
}

function parseMetaStringArray(metadata: Record<string, string> | null | undefined, key: string): string[] {
    if (!metadata?.[key]) return [];
    const raw = metadata[key].trim();
    if (!raw) return [];
    return raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
}

/**
 * Get interval from price.recurring (object or JSON string). Normalizes "yearly" -> "year".
 * Returns "one_time" when `recurring` is null/absent or `price.type === "one_time"` — a
 * genuinely one-time Stripe price must NEVER default to "month" (the live bug this fixes).
 */
function getRecurringInterval(
    recurring: StripePriceApi["recurring"],
    priceType?: StripePriceApi["type"],
): "month" | "year" | "one_time" {
    if (priceType === "one_time") return "one_time";
    if (recurring == null) return "one_time";
    const raw =
        typeof recurring === "string"
            ? (() => {
                  try {
                      const parsed = JSON.parse(recurring) as StripeRecurring;
                      return parsed?.interval ?? "month";
                  } catch {
                      return "month";
                  }
              })()
            : ((recurring as StripeRecurring)?.interval ?? "month");
    return raw === "year" || raw === "yearly" ? "year" : "month";
}

function normalizeToProductsWithPrices(
    raw: StripeProductsResponse,
): Array<{ p: StripeProductApi; prices: StripePriceApi[] }> {
    const items: (StripeProductWithPrices | StripeProductApi)[] = Array.isArray(raw)
        ? raw
        : ((raw as { products: (StripeProductWithPrices | StripeProductApi)[] }).products ?? []);
    return items.map((item) => {
        if ("product" in item && item.product) {
            const product = item.product as StripeProductApi;
            const prices = item.prices ?? (product as StripeProductApi).prices ?? [];
            return { p: product, prices: Array.isArray(prices) ? prices : [] };
        }
        const p = item as StripeProductApi;
        return { p, prices: p.prices ?? [] };
    });
}

function resolvePrice(product: StripeProductApi, pricesFromParent: StripePriceApi[]): StripePriceApi | null {
    const priceList = pricesFromParent?.length ? pricesFromParent : product.prices;
    if (priceList?.length) {
        const monthly = priceList.find((pr) => getRecurringInterval(pr.recurring, pr.type) === "month");
        if (monthly) return monthly;
        // A plan or add-on is a recurring subscription line, so a one-time price must never
        // become its price — that would sell a one-off charge as a subscription. The list
        // comes back from an unordered PostgREST select, so `priceList[0]` is nondeterministic
        // and can be the one-time row. Prefer any recurring price; only fall back to the first
        // entry when the product has nothing recurring at all (a hard failure here would drop
        // the whole catalog over one misconfigured product).
        // NOT a second copy of the one-time guard below. This picks a candidate; the
        // loop in fetchStripeProducts rejects a bad one. Both call getRecurringInterval,
        // the single classifier, so there is nothing here that can drift out of sync with
        // it. Whatever this returns is re-classified at the `const interval = ...` line
        // below and dropped by the `interval === "one_time"` guard if it is one-time, so
        // this fallback cannot leak a one-time price into a plan or add-on.
        const recurring = priceList.find((pr) => getRecurringInterval(pr.recurring, pr.type) !== "one_time");
        return recurring ?? priceList[0];
    }
    if (product.default_price && typeof product.default_price === "object" && "id" in product.default_price) {
        const def = product.default_price as unknown as StripePriceApi;
        return def;
    }
    if (product.default_price && typeof product.default_price === "string") {
        return {
            id: product.default_price,
            unit_amount: null,
            recurring: { interval: "month" } as StripeRecurring,
        };
    }
    return null;
}

/**
 * Fetch products from the API and normalize to ProductCatalog.
 * Products with metadata.type === "plan" go to plans; metadata.type === "addon" go to addons;
 * metadata.type === "one_time_fee" goes to one_time_fees. Any other/unrecognised
 * metadata.type is silently skipped — deliberate, do not add a catch-all branch.
 * metadata.max_discount (or discount_max) drives max_discount_percent.
 */
export async function fetchStripeProducts(): Promise<ProductCatalog> {
    const raw = expectJsonBody(
        await apiFetch<StripeProductsResponse>("stripe/products", { method: "GET" }),
        "GET stripe/products",
    );
    const normalized = normalizeToProductsWithPrices(raw);

    const plans: PlanProduct[] = [];
    const addons: AddonProduct[] = [];
    const oneTimeFees: OneTimeFeeProduct[] = [];

    for (const { p, prices: pricesFromApi } of normalized) {
        const meta = p.metadata ?? {};
        const type = (meta[METADATA_KEYS.TYPE] ?? "").toLowerCase();
        const discountMax =
            parseMetaNumber(meta, METADATA_KEYS.DISCOUNT_MAX) || parseMetaNumber(meta, METADATA_KEYS.DISCOUNT_MAX_ALT);

        // Build list of prices to iterate: use all API prices if available, otherwise resolve a single fallback
        let activePrices: StripePriceApi[];
        if (pricesFromApi.length > 0) {
            activePrices = pricesFromApi.filter((pr) => pr.active !== false && pr.unit_amount != null);
        } else {
            const fallback = resolvePrice(p, pricesFromApi);
            activePrices = fallback ? [fallback] : [];
        }

        for (const price of activePrices) {
            if (price.unit_amount == null) continue;

            const unitAmount =
                typeof price.unit_amount === "number"
                    ? price.unit_amount
                    : parseInt(String(price.unit_amount), 10) || 0;
            const interval = getRecurringInterval(price.recurring, price.type);

            // A plan or add-on is a recurring subscription line. If one of its prices is
            // one-time, it cannot be sold as either, and pushing it anyway would make the
            // `interval as "month" | "year"` casts below a lie: the entry carries the
            // runtime string "one_time" while claiming to be recurring. Today it merely
            // fails to match either tab and never renders, but the dead entry is the kind
            // of thing a future consumer trusts the type on. Fees are exempt - one-time is
            // exactly what they are.
            if (interval === "one_time" && type !== ONE_TIME_FEE) {
                // Warn rather than drop in silence. A plan or add-on whose only
                // active price is one-time is a Stripe misconfiguration, and
                // skipping it makes the product vanish from the rep's dropdown
                // with nothing to explain why - a missing plan looks like a bug
                // in this tool, and the person who can actually fix it is the
                // person who set the price up in Stripe.
                console.warn(
                    `[stripeProducts] Skipping ${type || "untyped"} product "${p.name ?? p.id}" (${p.id}): price ${price.id} is one-time, but a ${type} must be a recurring subscription price. Check the price's billing period in Stripe.`,
                );
                continue;
            }

            if (type === "plan") {
                const happyVisits = parseMetaNumber(meta, METADATA_KEYS.HAPPY_VISITS);
                plans.push({
                    stripe_product_id: p.id,
                    stripe_price_id: price.id,
                    name: p.name,
                    description: p.description ?? "",
                    unit_amount: unitAmount,
                    interval: interval as "month" | "year",
                    max_discount_percent: discountMax,
                    included_visits: parseMetaNumber(meta, METADATA_KEYS.INCLUDED_VISITS),
                    ...(happyVisits > 0 ? { happy_visits: happyVisits } : {}),
                });
            } else if (type === ONE_TIME_FEE) {
                const availableForPlans = parseMetaStringArray(meta, METADATA_KEYS.AVAILABLE_FOR_PLANS);
                oneTimeFees.push({
                    stripe_product_id: p.id,
                    stripe_price_id: price.id,
                    name: p.name,
                    unit_amount: unitAmount,
                    max_discount_percent: discountMax,
                    ...(availableForPlans.length > 0 ? { available_for_plans: availableForPlans } : {}),
                });
            } else if (type === "addon") {
                const availableForPlans = parseMetaStringArray(meta, METADATA_KEYS.AVAILABLE_FOR_PLANS);
                // Addon monthly visits per unit: from happy_visits, visits_per_unit, or included_visits metadata
                const addonHappyVisits = parseMetaNumber(meta, METADATA_KEYS.HAPPY_VISITS);
                const addonVisitsPerUnit = parseMetaNumber(meta, METADATA_KEYS.VISITS_PER_UNIT);
                const addonIncludedVisits = parseMetaNumber(meta, METADATA_KEYS.INCLUDED_VISITS);
                const visitsPerUnit =
                    addonHappyVisits > 0
                        ? addonHappyVisits
                        : addonVisitsPerUnit > 0
                          ? addonVisitsPerUnit
                          : addonIncludedVisits;
                addons.push({
                    stripe_product_id: p.id,
                    stripe_price_id: price.id,
                    name: p.name,
                    unit_amount: unitAmount,
                    interval: interval as "month" | "year",
                    max_discount_percent: discountMax,
                    ...(availableForPlans.length > 0 ? { available_for_plans: availableForPlans } : {}),
                    // Always set visits_per_unit and happy_visits so Total Monthly Visits can sum (addon amount × qty)
                    visits_per_unit: visitsPerUnit,
                    happy_visits: visitsPerUnit,
                });
            }
        }
    }

    const annualDiscount = !Array.isArray(raw)
        ? (raw as { annual_discount_percent?: number }).annual_discount_percent
        : undefined;

    return {
        plans,
        addons,
        one_time_fees: oneTimeFees,
        ...(annualDiscount != null ? { annual_discount_percent: annualDiscount } : {}),
    };
}
